import HOC from "../../components/HOC";
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { CircularProgress, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import OlinMascot from "../../assets/olinCharaLogin.png";
import { sendForgetPassword } from "../../services/authApi";
import FullsizeButton from "../../components/common/FullsizeButton";
import { IoArrowBack, IoInformationCircleOutline } from "react-icons/io5";
import * as yup from "yup";
import { useFormik } from "formik";
import ModalPopup from "../../components/common/ModalPopup";
import { BsCheckCircle } from "react-icons/bs";
import Logo from "../../assets/olinfull.png";

const validationSchema = yup.object({
  email: yup
    .string()
    .email("Format email tidak valid")
    .required("Email wajib diisi"),
});

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [openConfirm, setOpenConfirm] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema,
    onSubmit: (values) => {
      setErrorMessage("");
      mutateSendEmail(values.email);
    },
    validateOnMount: true,
  });

  const { mutate: mutateSendEmail, isPending } = useMutation({
    mutationFn: sendForgetPassword,
    onSuccess: () => {
      setOpenConfirm(true);
    },
    onError: (err) => {
      setErrorMessage(err.message?.id || "Gagal mengirim email reset password");
    },
  });

  return (
    <div className="bg-white relative">
      <FullsizeButton />
      <div className="flex flex-row justify-center h-screen">
        <div className="hidden md:order-last bg-cover lg:block lg:w-1/2">
          <div className="flex items-center h-full p-8 bg-white justify-center">
            <div className="w-[30vw] h-[30vw] flex justify-center object-cover items-center rounded-full p-8 bg-blue-100">
              <div className="p-16 bg-blue-300 rounded-full w-full h-full">
                <img
                  src={OlinMascot}
                  className="w-full h-full"
                  alt="Olin Mascot"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-1/2 flex justify-center lg:items-center px-0 lg:px-12 text-base">
          <div className="rounded-md p-4 lg:p-8 w-full h-fit">
            <div className="w-full flex items-center gap-4 lg:hidden text-xl">
              <div
                onClick={() => navigate(-1)}
                className="p-1 hover:bg-gray-200 rounded-full cursor-pointer"
              >
                <IoArrowBack className="text-gray-500" />
              </div>
            </div>

            <img
              src={Logo}
              alt="Logo OLIN"
              className={`my-8 ${
                isMobileView ? "w-[80px] mt-12" : "w-[100px] mt-16"
              }`}
            />
            <p
              className={`text-black ${
                isMobileView
                  ? "font-semibold text-xl mt-8"
                  : "font-bold text-3xl my-4"
              }`}
            >
              Lupa Password
            </p>
            <p
              className={`my-6 text-disableText ${
                isMobileView ? "text-base" : "text-lg"
              }`}
            >
              Masukkan email yang terdaftar. Kami akan mengirimkan instruksi
              untuk reset password.
            </p>

            <form onSubmit={formik.handleSubmit}>
              <div className="space-y-4">
                <div>
                  <p>Email</p>
                  <TextField
                    fullWidth
                    name="email"
                    type="email"
                    placeholder="Masukkan email"
                    variant="outlined"
                    value={formik.values.email}
                    onChange={(e) =>
                      formik.setFieldValue("email", e.target.value)
                    }
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={
                      formik.touched.email && formik.errors.email
                        ? formik.errors.email
                        : ""
                    }
                    disabled={isPending}
                    InputProps={{
                      sx: {
                        backgroundColor: "white",
                        borderRadius: "10px",
                        color: "#111827",
                        transition: "all 0.4s",
                        fontFamily: '"Montserrat", sans-serif',
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderWidth: "2px",
                          borderColor:
                            formik.touched.email && formik.errors.email
                              ? "#ef4444"
                              : "#d1d5db",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor:
                            formik.touched.email && formik.errors.email
                              ? "#ef4444"
                              : "#9ca3af",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#111827",
                          borderWidth: "2px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "14px 12px",
                          color: "#111827",
                          fontSize: { xs: "14px", lg: "16px" },
                          fontFamily: '"Montserrat", sans-serif',
                          "&::placeholder": {
                            color: "#CBCBCB",
                            opacity: 1,
                            fontFamily: '"Montserrat", sans-serif',
                          },
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#f9fafb",
                          opacity: 0.7,
                        },
                      },
                    }}
                    FormHelperTextProps={{
                      sx: {
                        width: "100%",
                        display: "flex",
                        justifyContent: "start",
                        margin: "2px 0 0 0",
                        fontSize: "12px",
                        color: "#ef4444",
                        fontFamily: '"Montserrat", sans-serif',
                      },
                    }}
                  />
                </div>

                {errorMessage && (
                  <div className="w-full flex justify-end mt-2">
                    <div className="flex items-center gap-2 bg-red-100 py-1.5 px-2.5 rounded-md">
                      <IoInformationCircleOutline
                        className="text-error shrink-0"
                        size={12}
                      />
                      <p className="text-sm text-red-700">{errorMessage}</p>
                    </div>
                  </div>
                )}

                <button
                  className="h-[45px] flex items-center justify-center bg-secondary text-white w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryHover shadow-lg transform duration-200 hover:scale-100 disabled:bg-disable disabled:cursor-not-allowed"
                  type="submit"
                  disabled={isPending || !formik.isValid || !formik.dirty}
                >
                  {isPending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <p>Kirim Link Reset</p>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <ModalPopup
        open={openConfirm}
        setOpen={setOpenConfirm}
        path="/login"
        title="Email Terkirim"
        subtitle="Email reset password berhasil dikirim. Silahkan cek dan ikuti instruksinya untuk melanjutkan."
        icon={
          <>
            <BsCheckCircle className="text-5xl text-green-500" />
          </>
        }
      />
    </div>
  );
};

export default HOC(ForgotPassword, "guest-only");
